import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Link,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
} from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { OnboardAction } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import ConversationalTemplate from "features/Auth/Onboarding/ConversationalTemplate";
import CreateTemplateForm from "features/Auth/Onboarding/CreateTemplateForm";
import EditTemplateFromScratch from "features/Auth/Onboarding/CreateTemplateForm/EditTemplateFromScratch";
import SocialMediaLinksForm from "features/Auth/Onboarding/SocialMediaLinksForm";
import {
  Template,
  TemplateContentType,
  TemplateMethod,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { hasSocialMediaProfile } from "utils/influencer";
import { trackEvent } from "utils/tracking";
import { useTracking } from "utils/useTracking";

import ReviewBentoAiTemplate from "./ReviewBentoAiTemplate";

const styles = {
  formContainer: {
    p: { xs: 3, sm: 5, md: 6 },
  },
  closeIcon: {
    color: "primary.main",
    fontSize: 14,
    position: "absolute",
    right: 10,
    top: 10,
  },
  noOverflow: {
    overflowX: "hidden",
  },
  fullWidth: {
    width: "100%",
  },
};

enum Page {
  pitchType = "pitchType",
  fromScratch = "fromScratch",
  bentoAi = "bentoAi",
  socials = "socials",
  template = "template",
  complete = "complete",
}

const CreateTemplateDialog = () => {
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const { profile, setProfile } = useContext(OrganizationUserContext);
  const { trackUserHasOnboarded } = useTracking();
  const {
    setCreateTemplateOnboardingDialogOpen,
    setProfileDialogOpen,
    setEmailBrandDialogOpen,
    incompleteProfile,
  } = useContext(OnboardingDialogsContext);
  const { slideInLeft, slideOutRight, slideInRight, slideProps } = useContext(
    SlideAnimationContext,
  );
  const [page, setPage] = useState<Page>(Page.pitchType);

  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);

  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [editTemplate, setEditTemplate] = useState<Template>({
    name: "",
    contentType: TemplateContentType.OUTREACH,
    subject: "",
    body: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    slideInLeft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = async (updatedForm?: Map) => {
    if (page === Page.fromScratch && !hasSocialMediaProfile(profile)) {
      setPage(Page.socials);
    } else if (
      page === Page.fromScratch ||
      page === Page.socials ||
      page === Page.template
    ) {
      if (isPage(routes.home)) {
        setPage(Page.complete);
      } else {
        trackUserHasOnboarded(OnboardAction.DIALOG_CREATED_TEMPLATE);
        setProfile((prev) => ({
          ...prev,
          reviewedFirstTemplate: true,
        }));
        setCreateTemplateOnboardingDialogOpen(false);
        await fetchOutreachTemplates();
      }
      return;
    }

    if (page === Page.bentoAi) {
      setEditTemplate(updatedForm?.outreach as Template);
      setPage(Page.template);
    }
  };

  const handleBack = async () => {
    trackEvent("Create Template Dialog Form - Back Button Pressed", {
      page,
      route: location.pathname,
    });

    await slideOutRight();
    if (page === Page.pitchType) {
      setCreateTemplateOnboardingDialogOpen(false);
      if (!isPage(routes.home)) {
        setProfileDialogOpen(true);
      }
    } else if (page === Page.fromScratch) {
      setPage(Page.pitchType);
    } else if (page === Page.bentoAi) {
      setPage(Page.bentoAi);
    } else if (page === Page.socials) {
      setPage(Page.fromScratch);
    }
    slideInRight();
  };

  const handleClose = () => {
    trackEvent("Create Template Dialog Form Closed", {
      page,
      route: location.pathname,
    });
    setCreateTemplateOnboardingDialogOpen(false);
  };

  const saveOrgProfile = async (onboardingForm: Map) => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...onboardingForm,
          page: location.pathname,
        },
      );
      handleNext();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      scroll="body"
      fullScreen={isMobileScreen}
      open={true}
      onClose={handleClose}
    >
      <IconButton sx={styles.closeIcon} onClick={handleClose}>
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <DialogContent sx={styles.noOverflow}>
        <Grid container sx={[styles.noOverflow, styles.fullWidth]}>
          <Slide
            in={slideProps.in}
            direction={slideProps.direction}
            easing={{
              enter: theme.transitions.easing.easeInOut,
              exit: theme.transitions.easing.easeIn,
            }}
            timeout={{
              enter: ENTER_DELAY,
              exit: EXIT_DELAY,
            }}
          >
            <Grid sx={[styles.formContainer, styles.fullWidth]} item xs={12}>
              {page === Page.pitchType && (
                <CreateTemplateForm
                  handleBack={handleBack}
                  handleNext={(method?: TemplateMethod | null) => {
                    if (method === TemplateMethod.from_scratch) {
                      setPage(Page.fromScratch);
                    } else {
                      setPage(Page.bentoAi);
                    }
                  }}
                />
              )}
              {page === Page.fromScratch && (
                <EditTemplateFromScratch
                  handleNext={handleNext}
                  handleBack={handleBack}
                  onboardingForm={onboardingForm}
                  setOnboardingForm={setOnboardingForm}
                />
              )}
              {page === Page.socials && (
                <SocialMediaLinksForm
                  onboardingForm={onboardingForm}
                  setOnboardingForm={setOnboardingForm}
                  handleNext={async () => {
                    await saveOrgProfile(onboardingForm);
                  }}
                  loading={loading}
                  handleBack={handleBack}
                  handleSkip={handleNext}
                />
              )}
              {page === Page.bentoAi && (
                <ConversationalTemplate
                  handleBack={handleBack}
                  handleNext={handleNext}
                />
              )}
              {page === Page.template && (
                <ReviewBentoAiTemplate
                  editTemplate={editTemplate}
                  setEditTemplate={setEditTemplate}
                  handleBack={handleBack}
                  handleNext={handleNext}
                />
              )}

              {page === Page.complete && (
                <>
                  <Typography
                    variant="h4"
                    paragraph
                    sx={{ textAlign: "center" }}
                  >
                    <Box
                      component="i"
                      color="primary.main"
                      sx={{ pr: 2 }}
                      className="fa-solid fa-party-horn"
                    />
                    Template Created!
                  </Typography>
                  <Typography paragraph sx={{ textAlign: "center" }}>
                    Congrats on creating your first template! This gives you a
                    ready-to-use message that makes reaching out to brands
                    faster and easier.
                  </Typography>
                  <Typography gutterBottom sx={{ textAlign: "center" }}>
                    Want create or edit your templates? You can further
                    customize your templates in the{" "}
                    <Link
                      component={RouterLink}
                      to={`/${routes.templates}`}
                      onClick={() => {
                        trackEvent(
                          "Create Template Dialog Form - Open Templates Page",
                          {
                            page,
                            route: location.pathname,
                          },
                        );
                        setCreateTemplateOnboardingDialogOpen(false);
                      }}
                      sx={{ textDecoration: "none" }}
                    >
                      <strong>template page</strong>
                    </Link>
                    .
                  </Typography>
                  {isPage(routes.home) && incompleteProfile && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography
                        paragraph
                        variant="h4"
                        sx={{ textAlign: "center" }}
                      >
                        Next Step: Complete Your Profile
                      </Typography>
                      <Typography paragraph sx={{ textAlign: "center" }}>
                        To help you pitch brands effectively, fill in a few
                        details to get personalized brand recommendations
                        tailored to your content.
                      </Typography>
                      <Box display="flex" justifyContent="center">
                        <Button
                          disableElevation
                          sx={{ textAlign: "center" }}
                          variant="contained"
                          onClick={() => {
                            trackEvent(
                              "Create Template Dialog Form - Complete Profile Started",
                              {
                                page,
                                route: location.pathname,
                              },
                            );
                            setCreateTemplateOnboardingDialogOpen(false);
                            setProfileDialogOpen(true);
                          }}
                        >
                          Start
                        </Button>
                      </Box>
                    </>
                  )}
                  {isPage(routes.home) &&
                    !incompleteProfile &&
                    !profile?.hasOnboarded && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Typography
                          paragraph
                          variant="h4"
                          sx={{ textAlign: "center" }}
                        >
                          Next Step: Send Your First Pitch
                        </Typography>
                        <Typography paragraph sx={{ textAlign: "center" }}>
                          To help you pitch brands effectively, let's guide you
                          through sending your first brand pitch using Bento.
                        </Typography>
                        <Box display="flex" justifyContent="center">
                          <Button
                            disableElevation
                            sx={{ textAlign: "center" }}
                            variant="contained"
                            onClick={() => {
                              trackEvent(
                                "Create Template Dialog Form - Send First Email Started",
                                {
                                  page,
                                  route: location.pathname,
                                },
                              );
                              setCreateTemplateOnboardingDialogOpen(false);
                              setEmailBrandDialogOpen(true);
                            }}
                          >
                            Start
                          </Button>
                        </Box>
                      </>
                    )}
                </>
              )}
            </Grid>
          </Slide>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTemplateDialog;
